import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, PieChart, BarChart3, CheckCircle, ArrowRight, Calculator, Zap, Target, Shield } from 'lucide-react';
import SEO, { SEOPages } from '../components/SEO';

const Investissements = () => {
  // État pour le calculateur d'intérêts composés
  const [calculator, setCalculator] = useState({
    capitalInitial: 10000,
    versementMensuel: 500,
    dureeAnnees: 10,
    tauxRendement: 5
  });

  const [calculatorResult, setCalculatorResult] = useState(null);

  const investmentServices = [
    {
      icon: TrendingUp,
      title: "Placements diversifiés",
      description: "Portefeuilles d'investissement adaptés à votre profil et objectifs",
      benefits: ["Diversification optimale", "Gestion des risques", "Stratégies long terme"]
    },
    {
      icon: PieChart,
      title: "Gestion de patrimoine",
      description: "Approche globale pour faire fructifier et protéger votre patrimoine",
      benefits: ["Vision d'ensemble", "Optimisation fiscale", "Transmission patrimoine"]
    },
    {
      icon: BarChart3,
      title: "Investissements durables",
      description: "Solutions ESG pour investir de manière responsable et rentable",
      benefits: ["Impact positif", "Performance durable", "Critères ESG stricts"]
    },
    {
      icon: Shield,
      title: "Protection du capital",
      description: "Stratégies de préservation et de croissance sécurisée du patrimoine",
      benefits: ["Capital garanti", "Couverture risques", "Produits structurés"]
    }
  ];

  const processSteps = [
    {
      number: "1",
      title: "Analyse de profil",
      description: "Évaluation complète de votre situation financière et de vos objectifs"
    },
    {
      number: "2", 
      title: "Stratégie personnalisée",
      description: "Création d'une stratégie d'investissement sur mesure"
    },
    {
      number: "3",
      title: "Mise en œuvre",
      description: "Implémentation de votre portefeuille avec nos partenaires sélectionnés"
    },
    {
      number: "4",
      title: "Suivi et optimisation",
      description: "Monitoring continu et ajustements selon l'évolution des marchés"
    }
  ];

  const calculateCompoundInterest = (withScroll = false) => {
    const P = parseFloat(calculator.capitalInitial) || 0;
    const PMT = parseFloat(calculator.versementMensuel) || 0; // Permet 0 ou vide
    const r = parseFloat(calculator.tauxRendement) / 100 / 12; // taux mensuel
    const n = parseFloat(calculator.dureeAnnees) * 12; // nombre de mois

    // Capital final avec intérêts composés (capital initial)
    const futureValueCapital = P * Math.pow(1 + r, n);
    
    // Valeur future des versements mensuels (si il y en a)
    const futureValuePayments = PMT > 0 ? PMT * ((Math.pow(1 + r, n) - 1) / r) : 0;
    
    const capitalFinal = futureValueCapital + futureValuePayments;
    const totalVerse = P + (PMT * n);
    const interetsGagnes = capitalFinal - totalVerse;

    // Calcul avec compte épargne à 0%
    const epargneZero = totalVerse;
    const manqueAGagner = capitalFinal - epargneZero;

    setCalculatorResult({
      capitalFinal: Math.round(capitalFinal),
      totalVerse: Math.round(totalVerse),
      interetsGagnes: Math.round(interetsGagnes),
      epargneZero: Math.round(epargneZero),
      manqueAGagner: Math.round(manqueAGagner),
      hasMonthlyPayments: PMT > 0 // Pour adapter l'affichage
    });

    // Scroll vers le calculateur seulement si demandé (bouton hero)
    if (withScroll) {
      const calculatorSection = document.getElementById('calculateur-interets-composes');
      if (calculatorSection) {
        calculatorSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  // Fonction pour le bouton hero (avec scroll)
  const scrollToCalculator = () => {
    calculateCompoundInterest(true);
  };

  // Fonction pour le bouton dans le calculateur (sans scroll)
  const calculateOnly = () => {
    calculateCompoundInterest(false);
  };

  const handleCalculatorChange = (field, value) => {
    setCalculator(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="pt-20">
      <SEO {...SEOPages.investissements} />
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          role="img"
          aria-label="Gestion de patrimoine et investissements suisses - ETF, actions et portefeuilles diversifiés"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBtYXJrZXRzfGVufDB8fHx8MTc1NzM0NDM1NHww&ixlib=rb-4.1.0&q=85)',
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold heading-display text-white mb-6">
              Investissements : <span className="text-yellow-300">faites fructifier</span> votre patrimoine
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              <span className="text-red-300 font-semibold">Laisser votre argent dormir sur un compte courant, c'est choisir un rendement de 0%.</span> 
              {" "}Avec un taux d'inflation historique de 2% en Suisse, votre pouvoir d'achat diminue chaque année. 
              Investissez intelligemment avec ATOM Group SA.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-8 py-4 flex items-center justify-center">
                Analyser mon patrimoine
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <button 
                onClick={scrollToCalculator}
                className="btn-glass text-lg px-8 py-4 flex items-center justify-center"
              >
                <Calculator className="mr-2" size={20} />
                Calculateur d'intérêts composés
              </button>
            </div>

            {/* Statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-red-400 mb-2">2.26%</div>
                <div className="text-gray-300">Taux d'inflation moyen historique en Suisse</div>
              </div>
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-green-400 mb-2">3-8%</div>
                <div className="text-gray-300">Rendement moyen d'un placement</div>
              </div>
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-white mb-2">+300</div>
                <div className="text-gray-300">Solutions d'investissements</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculateur d'intérêts composés */}
      <section id="calculateur-interets-composes" className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Calculateur d'intérêts composés
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Découvrez la puissance des intérêts composés et visualisez la croissance de votre patrimoine avec ATOM Group SA
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="glass p-8 rounded-3xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Paramètres */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-white mb-6">Vos paramètres d'investissement</h3>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Capital initial (CHF)</label>
                    <input
                      type="number"
                      value={calculator.capitalInitial}
                      onChange={(e) => handleCalculatorChange('capitalInitial', e.target.value)}
                      className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300"
                      min="0"
                      step="1000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Versement mensuel (CHF) - <span className="text-gray-400">Optionnel</span></label>
                    <input
                      type="number"
                      value={calculator.versementMensuel}
                      onChange={(e) => handleCalculatorChange('versementMensuel', e.target.value)}
                      className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300"
                      min="0"
                      step="100"
                      placeholder="0 (aucun versement mensuel)"
                    />
                    <p className="text-xs text-gray-400 mt-1">Laissez vide ou à 0 pour calculer uniquement avec le capital initial</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Durée (années)</label>
                    <input
                      type="number"
                      value={calculator.dureeAnnees}
                      onChange={(e) => handleCalculatorChange('dureeAnnees', e.target.value)}
                      className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300"
                      min="1"
                      max="50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Rendement annuel moyen (%)</label>
                    <input
                      type="number"
                      value={calculator.tauxRendement}
                      onChange={(e) => handleCalculatorChange('tauxRendement', e.target.value)}
                      className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300"
                      min="0"
                      max="20"
                      step="0.1"
                    />
                  </div>
                  
                  <button
                    onClick={calculateOnly}
                    className="w-full btn-glass btn-primary text-lg py-4 flex items-center justify-center"
                  >
                    <Zap className="mr-2" size={20} />
                    Calculer ma croissance patrimoniale
                  </button>
                </div>
                
                {/* Résultats */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-white mb-6">Vos résultats</h3>
                  
                  {calculatorResult ? (
                    <div className="space-y-4">
                      <div className="glass p-6 rounded-2xl border-2 border-green-500/30">
                        <div className="text-sm text-gray-300">Capital final avec ATOM Group SA</div>
                        <div className="text-3xl font-bold text-green-400">
                          CHF {calculatorResult.capitalFinal.toLocaleString()}
                        </div>
                      </div>
                      
                      <div className="glass p-6 rounded-2xl">
                        <div className="text-sm text-gray-300">
                          {calculatorResult.hasMonthlyPayments ? 'Total versé' : 'Capital de départ'}
                        </div>
                        <div className="text-2xl font-bold text-white">
                          CHF {calculatorResult.totalVerse.toLocaleString()}
                        </div>
                      </div>
                      
                      <div className="glass p-6 rounded-2xl">
                        <div className="text-sm text-gray-300">Intérêts gagnés</div>
                        <div className="text-2xl font-bold text-yellow-400">
                          CHF {calculatorResult.interetsGagnes.toLocaleString()}
                        </div>
                      </div>
                      
                      <div className="glass p-6 rounded-2xl border-2 border-red-500/30">
                        <div className="text-sm text-gray-300">
                          {calculatorResult.hasMonthlyPayments ? 'Avec un compte épargne à 0%' : 'Sans investissement (épargne 0%)'}
                        </div>
                        <div className="text-xl font-bold text-red-400">
                          CHF {calculatorResult.epargneZero.toLocaleString()}
                        </div>
                        <div className="text-sm text-red-300 mt-2">
                          Vous perdez CHF {calculatorResult.manqueAGagner.toLocaleString()} !
                        </div>
                      </div>
                      
                      <div className="glass p-4 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20">
                        <div className="text-center">
                          <div className="text-lg font-semibold text-white mb-2">
                            {calculatorResult.hasMonthlyPayments ? 'Multiplication de votre épargne' : 'Croissance de votre capital'}
                          </div>
                          <div className="text-2xl font-bold text-yellow-400">
                            x{(calculatorResult.capitalFinal / calculatorResult.epargneZero).toFixed(1)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="glass p-12 rounded-2xl text-center">
                      <Target size={48} className="text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-300">
                        Cliquez sur "Calculer ma croissance patrimoniale" pour voir vos résultats
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {calculatorResult && (
                <div className="mt-8 text-center">
                  <div className="glass p-6 rounded-2xl">
                    <h4 className="text-xl font-semibold text-white mb-4">
                      Et si votre épargne commençait enfin à travailler pour vous ?
                    </h4>
                    <p className="text-gray-300 mb-6">
                      Avec ATOM Group SA, bénéficiez d'un accompagnement expert pour maximiser le potentiel de vos investissements
                    </p>
                    <Link to="/contact" className="btn-glass btn-primary px-8 py-3">
                      Prendre rendez-vous
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services d'investissement */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Nos services d'investissement
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Une approche globale pour optimiser votre patrimoine
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {investmentServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="glass p-8 group">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-4">{service.title}</h3>
                  <p className="text-gray-300 mb-6">{service.description}</p>
                  
                  <ul className="space-y-2">
                    {service.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-300">
                        <CheckCircle size={16} className="text-green-400 mr-2 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Processus d'accompagnement */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Notre processus d'accompagnement
            </h2>
            <p className="text-xl text-gray-300">
              Une méthode éprouvée en 4 étapes pour optimiser vos investissements
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="glass p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">{step.number}</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">{step.title}</h3>
                <p className="text-gray-300">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <div className="glass p-12 rounded-3xl text-center">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Prêt à faire fructifier votre patrimoine ?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Ne laissez plus votre argent perdre de sa valeur. Découvrez comment nos stratégies 
              d'investissement peuvent transformer votre avenir financier.
            </p>
            <div className="flex justify-center">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-12 py-4">
                Prendre rendez-vous
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Investissements;